import type { ComponentType } from "react";
import { cn } from "@/lib/utils";
import type { CaseStudyKind } from "@/config/case-studies";

function StrokeIcon({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={cn("shrink-0 text-[var(--primary)]", className)}
    >
      {children}
    </svg>
  );
}

/** Full product / platform build */
export function IconCaseProduct({ className }: { className?: string }) {
  return (
    <StrokeIcon className={className}>
      <path d="M4 6h16v6H4z" />
      <path d="M7 14h10v4H7z" />
      <path d="M9 4v2M15 4v2" />
    </StrokeIcon>
  );
}

/** Integrations & data glue */
export function IconCaseIntegration({ className }: { className?: string }) {
  return (
    <StrokeIcon className={className}>
      <circle cx="7" cy="12" r="2.5" />
      <circle cx="17" cy="8" r="2.5" />
      <circle cx="17" cy="16" r="2.5" />
      <path d="M9.2 10.5 14.5 9M9.2 13.5 14.5 15" />
    </StrokeIcon>
  );
}

/** Hosted services, MCP, infrastructure */
export function IconCasePlatform({ className }: { className?: string }) {
  return (
    <StrokeIcon className={className}>
      <rect x="3" y="4" width="18" height="8" rx="1.5" />
      <path d="M7 8h.01M12 8h.01M17 8h.01" />
      <path d="M6 16h12" />
      <path d="M9 20h6" />
    </StrokeIcon>
  );
}

/** Many parallel deliveries */
export function IconCaseAggregate({ className }: { className?: string }) {
  return (
    <StrokeIcon className={className}>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </StrokeIcon>
  );
}

export function IconSectionContext({ className }: { className?: string }) {
  return (
    <StrokeIcon className={cn("text-[color-mix(in_srgb,var(--primary)_75%,var(--on-surface-variant)_25%)]", className)}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M8 13h8M8 17h6" />
    </StrokeIcon>
  );
}

export function IconSectionApproach({ className }: { className?: string }) {
  return (
    <StrokeIcon className={cn("text-[color-mix(in_srgb,var(--primary)_75%,var(--on-surface-variant)_25%)]", className)}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
      <path d="M4.93 4.93l2.12 2.12M16.95 16.95l2.12 2.12M4.93 19.07l2.12-2.12M16.95 7.05l2.12-2.12" />
    </StrokeIcon>
  );
}

export function IconSectionOutcomes({ className }: { className?: string }) {
  return (
    <StrokeIcon className={cn("text-[color-mix(in_srgb,var(--primary)_75%,var(--on-surface-variant)_25%)]", className)}>
      <path d="M12 20V10" />
      <path d="M18 20V4" />
      <path d="M6 20v-6" />
    </StrokeIcon>
  );
}

const kindIcon: Record<
  CaseStudyKind,
  ComponentType<{ className?: string }>
> = {
  product: IconCaseProduct,
  integration: IconCaseIntegration,
  platform: IconCasePlatform,
  aggregate: IconCaseAggregate,
};

export function CaseStudyKindIcon({
  kind,
  className,
}: {
  kind: CaseStudyKind;
  className?: string;
}) {
  const Icon = kindIcon[kind];
  return <Icon className={className} />;
}
